import { useMutation, useQuery } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import produce from 'immer';
import { useDispatch } from 'react-redux';
import { setElements, useReviewCreationData, useElements } from 'reducers/reviewCreationSlice';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { useReviewCreationNavigation } from '../useReviewCreationNavigation';

const getElements = ({ reviewCycleId }) => {
  const url = endpoints.reviews.reviewCycleCreation.elements;
  return sendRequest({
    url,
    credentials: 'include',
    queryParams: {
      review_cycle_id: reviewCycleId,
    },
  });
};

const getTemplateList = ({ includeSample = true }) => {
  const url = endpoints.reviews.reviewCycleCreation.templateList;
  return sendRequest({
    url,
    credentials: 'include',
    queryParams: {
      include_sample: includeSample,
    },
  });
};

const putElements = ({ reviewCycleId, elements }) => {
  const url = endpoints.reviews.reviewCycleCreation.elements;
  return sendRequest({
    url,
    method: 'PUT',
    credentials: 'include',
    queryParams: {
      review_cycle_id: reviewCycleId,
    },
    body: {
      elements,
    },
  });
};

export const useGetElements = ({ reviewCycleId }) => {
  const dispatch = useDispatch();
  return useQuery(
    [REACT_QUERY_KEYS.reviewCreation.ELEMENTS, reviewCycleId],
    () =>
      getElements({ reviewCycleId }).then((res) => {
        const { elements } = res.entity;
        dispatch(setElements(elements));

        return res.entity;
      }),
    { enabled: !!reviewCycleId }
  );
};

export const useSaveElements = () => {
  const { goToNextStep, goToStep } = useReviewCreationNavigation();
  const elements = useElements();
  const { reviewCycleId } = useReviewCreationData();

  // TODO: v1 or later
  // const settingsForFeedbackFormIdsMap = new Map();
  // elements.forEach((setting) => {
  //   settingsForFeedbackFormIdsMap.set(setting.feedbackFormId, setting.elementSettings);
  // });

  // const copiedSettingsFromViewIds = elements.map((setting) => {
  //   if (
  //     !setting.isAllowSameSettingAsFirstView ||
  //     setting.settingSameAsFormId === null ||
  //     !settingsForFeedbackFormIdsMap.has(setting.settingSameAsFormId)
  //   ) {
  //     return setting;
  //   }
  //   const copiedSetting = settingsForFeedbackFormIdsMap.get(setting.settingSameAsFormId);
  //   return { ...setting, elementSettings: copiedSetting };
  // });

  // add feedback form Id to score settings
  const copiedSettingsFromViewIdsWithFeedbackId = produce(elements, (draft) => {
    draft.forEach((setting) => {
      setting.elementSettings.elementScoreSettings.autoComputeGoalRatingSettings?.forEach((scoreSetting) => {
        // eslint-disable-next-line no-param-reassign
        scoreSetting.feedbackFormId = setting.feedbackFormId;
      });
    });
  });

  return useMutation({
    mutationFn: () =>
      putElements({
        reviewCycleId,
        elements: copiedSettingsFromViewIdsWithFeedbackId,
      }),
    onSuccess: (_data, variables) => {
      if (variables?.nextStep) {
        goToStep(variables.nextStep);
        return;
      }
      // todo: navigate to next step
      goToNextStep();
      // --------------------------------
      // queryClient.invalidateQueries([REACT_QUERY_KEYS.views.views]);
      // queryClient
      //   .invalidateQueries([REACT_QUERY_KEYS.views.sidebarViewCohorts, module])
      //   .then(() => {
      //     applyFilters(history, location, data);
      //   });
      // // applyFilters(history, location, data);
      // dispatch(setSaveViewErrorMessage(null));
      // dispatch(setIsSaveViewModalOpen(false));
      // // dispatch(setViewsSubScreen(null));
    },
    onError: () => {
      // if (error?.message) {
      //   dispatch(setSaveViewErrorMessage(error.message));
      // }
      // dispatch(setIsSaveViewModalOpen(false));
    },
  });
};

export const useGetTemplateList = () =>
  useQuery(
    [REACT_QUERY_KEYS.reviewCreation.templateList],
    () => getTemplateList({ includeSample: true }).then((res) => res.entity),
    { refetchOnWindowFocus: true }
  );
